/**
 * DynamicTable V2 — external → internal schema normalization (pure, no side effects).
 */

import { COLUMN_WIDTH_DEFAULTS, COLUMN_TYPE_WIDTHS, FEATURES_DEFAULTS } from './defaults';
import { ACCEPT_DECIMAL, CURRENCY_DECIMAL } from '@packages/shared/constants';
import type {
  ActionConfig,
  ActionDef,
  AggregateDef,
  ColumnType,
  FormatConfig,
  ConfirmConfig,
  DataSourceConfig,
  DataSourceDef,
  DataType,
  EditableDef,
  ExternalColumnDef,
  ExternalTableSchema,
  FeaturesConfig,
  FilterDef,
  InternalActionDef,
  InternalColumnDef,
  InternalConfirmConfig,
  InternalDataInjection,
  InternalDataSourceConfig,
  InternalFeaturesConfig,
  InternalSubRowConfig,
  InternalTableSchema,
  InternalToolbarButton,
  InternalToolbarTemplate,
  ToolbarButtonDef,
  ToolbarTemplateDef,
  ContinuationCursorConfig,
  OnSuccessAction,
  SubRowConfig,
  ToolbarGroupDef,
} from './types';

/** Hook for future plugin-driven enrichment; ignored by this pipeline. */
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface PluginRegistry { }

const AGGREGATE_FNS: ReadonlyArray<AggregateDef['fn']> = ['sum', 'average', 'count', 'min', 'max'];

/**
 * Normalize column visibility to the canonical 3-value model.
 *
 * Canonical values (what schema authors write):
 *   omit / undefined → always visible, not in picker  (most columns — write nothing)
 *   "locked"         → always visible, not in picker  (key identity columns)
 *   "picker"         → hidden until user adds via column picker
 *   "hidden"         → never shown, never in picker
 *
 * Backwards-compat aliases (still accepted, normalised away):
 *   "mandatory" → "locked"
 *   "default"   → omit (treated as undefined → visible by default)
 *   "optional"  → "picker"
 *   "internal"  → "hidden"
 */
function normalizeVisibility(
  v: string | undefined,
): 'locked' | 'picker' | 'hidden' {
  switch (v) {
    case 'locked':
    case 'mandatory': return 'locked';
    case 'picker':
    case 'optional': return 'picker';
    case 'hidden':
    case 'internal': return 'hidden';
    // "default" and undefined → always visible, treated as locked-light
    default: return 'locked';
  }
}

function humanizeField(field: string): string {
  if (!field) return '';
  return field
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .trim()
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

function slugify(label: string): string {
  const s = label
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return s || 'action';
}

function inferColumnType(col: ExternalColumnDef): ColumnType {
  if (col.type) return col.type;
  if (col.badge) return 'badge';
  if (col.couple) return 'couple';
  if (col.dateRange) return 'dateRange';
  if (col.mask) return 'mask';
  if (col.sensitiveReveal) return 'sensitiveReveal';
  if (col.link) return 'link';
  if (col.modal) return 'modal';
  if (col.editable && typeof col.editable === 'object') {
    if (col.editable.type === 'date') return 'date';
    if (col.editable.type === 'number') return 'number';
  }
  return 'text';
}

function inferDataType(type: ColumnType): DataType {
  const map: Record<ColumnType, DataType> = {
    text: 'string',
    html: 'string',
    date: 'date',
    dateTime: 'date',
    dateTimeLTS: 'date',
    conditionalDate: 'date',
    number: 'number',
    currency: 'number',
    percentage: 'number',
    badge: 'string',
    couple: 'object',
    coupleWithDateRange: 'object',
    link: 'string',
    modal: 'string',
    mask: 'string',
    sensitiveReveal: 'string',
    conditional: 'string',
    dateRange: 'object',
    time: 'string',
    arraySplit: 'array',
    space: 'string',
    custom: 'string',
  };
  return map[type] ?? 'string';
}

function normalizeWidth(
  width: ExternalColumnDef['width'] | null | undefined,
  columnType?: ColumnType
): { min: number; max: number; default: number } {
  // Pick type-aware seed values; fall back to global defaults
  const typeSeed =
    columnType != null
      ? COLUMN_TYPE_WIDTHS[columnType] ?? COLUMN_WIDTH_DEFAULTS
      : COLUMN_WIDTH_DEFAULTS;

  if (width == null) {
    return { min: typeSeed.min, max: typeSeed.max, default: typeSeed.default };
  }
  if (typeof width === 'number') {
    const def = Number.isFinite(width) && width >= 0 ? width : typeSeed.default;
    return { min: typeSeed.min, max: typeSeed.max, default: def };
  }
  let min = width.min ?? typeSeed.min;
  let max = width.max ?? typeSeed.max;
  let def =
    Number.isFinite(width.default) && (width.default ?? -1) >= 0
      ? (width.default as number)
      : typeSeed.default;
  if (min > max) [min, max] = [max, min];
  def = Math.min(max, Math.max(min, def));
  return { min, max, default: def };
}

function normalizeEditable(
  editable: ExternalColumnDef['editable'] | null | undefined
): InternalColumnDef['editable'] {
  if (editable === false || editable == null) return null;
  if (editable === true) {
    return {
      type: 'text',
      placeholder: '',
      options: [],
      defaultValueField: null,
      validation: null,
      when: null,
      multiple: false,
      width: null,
      precision: null,
      rowOptionsField: null,
      rowOptionsLabelField: 'label',
      rowOptionsValueField: 'value',
      effectToFields: [],
    };
  }
  const e = editable as EditableDef;
  return {
    type: e.type,
    placeholder: e.placeholder ?? '',
    options: [...(e.options ?? [])],
    defaultValueField: e.defaultValueField ?? null,
    validation: e.validation ?? null,
    when: e.when ?? null,
    multiple: e.multiple === true,
    width: e.width ?? null,
    precision: e.precision ?? null,
    rowOptionsField: e.rowOptionsField ?? null,
    rowOptionsLabelField: e.rowOptionsLabelField ?? 'label',
    rowOptionsValueField: e.rowOptionsValueField ?? 'value',
    effectToFields: e.effectToFields ? [...e.effectToFields] : [],
  };
}

function normalizeAggregate(
  aggregate: ExternalColumnDef['aggregate'],
  _type: ColumnType
): AggregateDef | null {
  if (aggregate == null) return null;
  if (typeof aggregate === 'string') {
    const fn = aggregate as AggregateDef['fn'];
    return AGGREGATE_FNS.includes(fn) ? { fn } : null;
  }
  return {
    fn: aggregate.fn,
    ...(aggregate.format !== undefined ? { format: { ...aggregate.format } } : {}),
    ...(aggregate.label !== undefined ? { label: aggregate.label } : {}),
  };
}

const DEFAULT_FORMAT_BY_TYPE: Partial<Record<ColumnType, FormatConfig>> = {
  currency: { decimals: CURRENCY_DECIMAL, trimTrailingZeros: true },
  percentage: { decimals: CURRENCY_DECIMAL, trimTrailingZeros: true },
};

function resolveDefaultFormat(
  type: ColumnType,
  explicit: FormatConfig | undefined | null,
  skipDefault?: boolean,
): Readonly<FormatConfig> | null {
  if (explicit != null) return { ...explicit };
  if (skipDefault) return null;
  return DEFAULT_FORMAT_BY_TYPE[type] != null ? { ...DEFAULT_FORMAT_BY_TYPE[type] } : null;
}

function normalizeColumnDateRange(
  type: ColumnType,
  raw: ExternalColumnDef['dateRange'] | null | undefined,
): { startField: string; endField: string } | null {
  if (type !== 'dateRange' && type !== 'coupleWithDateRange') return null;
  if (raw == null) return null;
  const legacy = raw as { startField?: string; endField?: string; start?: string; end?: string };
  const startField = legacy.startField ?? legacy.start ?? '';
  const endField = legacy.endField ?? legacy.end ?? '';
  if (startField === '' && endField === '') return null;
  return { startField, endField };
}

function normalizeColumn(col: ExternalColumnDef): InternalColumnDef {
  const type = inferColumnType(col);
  const dataType = col.dataType ?? inferDataType(type);
  const header = (
    col.header != null && String(col.header).trim() !== '' ? col.header : humanizeField(col.field)
  ) as string;

  const coupleAllowed = type === 'couple' || type === 'coupleWithDateRange';

  return {
    id: col.field,
    header,
    field: col.field,
    description: col.description ?? `${header} column displaying ${type} data`,
    type,
    dataType,
    width: normalizeWidth(col.width, type),
    align: col.align ?? 'left',
    sortable: col.sortable ?? false,
    visibility: normalizeVisibility(col.visibility),
    group: col.group ?? null,
    emptyValue: col.emptyDisplay ?? '-',
    format: resolveDefaultFormat(type, col.format, col.skipFormatDefault),
    badge:
      type === 'badge' && col.badge != null
        ? { ...col.badge, statusMap: { ...col.badge.statusMap } }
        : null,
    couple: coupleAllowed && col.couple != null ? { ...col.couple } : null,
    link: type === 'link' && col.link != null ? { ...col.link } : null,
    mask: type === 'mask' && col.mask != null ? { ...col.mask } : null,
    sensitiveReveal:
      type === 'sensitiveReveal' && col.sensitiveReveal != null
        ? {
          maskFormat: col.sensitiveReveal.maskFormat ?? 'partial',
          maskType: col.sensitiveReveal.maskType,
          idField: col.sensitiveReveal.idField,
          endpoint: col.sensitiveReveal.endpoint,
          method: col.sensitiveReveal.method ?? 'GET',
          paramName: col.sensitiveReveal.paramName ?? col.sensitiveReveal.idField,
          responseField: col.sensitiveReveal.responseField ?? '',
          dateFormat: col.sensitiveReveal.dateFormat,
          presenceField: col.sensitiveReveal.presenceField,
          placeholder: col.sensitiveReveal.placeholder,
          extraRowFields: col.sensitiveReveal.extraRowFields ?? undefined,
        }
        : null,
    modal: type === 'modal' && col.modal != null ? { ...col.modal } : null,
    dateRange: normalizeColumnDateRange(type, col.dateRange),
    conditionalValues: col.conditionalValues != null ? { ...col.conditionalValues } : null,
    fields: col.fields != null ? { ...col.fields } : null,
    editable: normalizeEditable(col.editable),
    clearOnEdit: [...(col.clearOnEdit ?? [])],
    customRenderer: col.renderer ?? null,
    aggregate: normalizeAggregate(col.aggregate, type),
    hideOnSubRows: col.hideOnSubRows ?? false,
    prefix: col.prefix ?? null,
    uppercase: col.uppercase ?? false,
    pinned: col.fixed ?? null,
    actionIntent: col.actionIntent != null
      ? {
          actionId: col.actionIntent.actionId,
          payload: { ...col.actionIntent.payload },
          whenHasValue: col.actionIntent.whenHasValue ?? true,
          mode: col.actionIntent.mode,
          label: col.actionIntent.label,
        }
      : null,
    conditionalDisplay: col.conditionalDisplay != null
      ? col.conditionalDisplay.map((rule) => ({
        when: { ...rule.when },
        style: rule.style,
        show: rule.show,
        as: rule.as ?? 'badge',
        variant: rule.variant ?? 'default',
      }))
      : null,
  };
}

function normalizeFeatures(
  external: FeaturesConfig | null | undefined,
  subRows: InternalSubRowConfig | null,
  columns: InternalColumnDef[],
  bulkActions: ActionDef[]
): InternalFeaturesConfig {
  const base = FEATURES_DEFAULTS;
  const ext = external ?? {};
  const pag = ext.pagination ?? {};
  const ed = ext.editing ?? {};
  const rowsExt = ext.rows ?? {};

  // ── Column preferences ────────────────────────────────────────────────────
  // Accept new `columnPreferences` key first; fall back to legacy `columnManagement`
  // for backwards-compat.  Selector is only enabled when a persistence key exists
  // AND the schema actually declares optional columns (otherwise the picker is useless).
  const hasOptionalColumns = columns.some((c) => c.visibility === 'picker');

  let columnPreferences: InternalFeaturesConfig['columnPreferences'] = null;

  if (ext.columnPreferences?.persistenceKey) {
    columnPreferences = {
      persistenceKey: ext.columnPreferences.persistenceKey,
      selectorEnabled: hasOptionalColumns,
      reorderable: ext.columnPreferences.reorderable ?? false,
      resizable: ext.columnPreferences.resizable ?? false,
    };
  } else if (ext.columnManagement?.persistence?.enabled && ext.columnManagement.persistence.key) {
    columnPreferences = {
      persistenceKey: ext.columnManagement.persistence.key,
      selectorEnabled: hasOptionalColumns,
      reorderable: ext.columnManagement.reorderable ?? false,
      resizable: ext.columnManagement.resizable ?? false,
    };
  }

  // ── Selection ─────────────────────────────────────────────────────────────
  // `selection` presence in the external schema = feature enabled.
  // All derived flags are computed here — schema authors never write them.
  //
  //   showCheckbox      ← always true when selection is present
  //   multiSelect       ← always true (single-select uses row callbacks, not this feature)
  //   selectAllPages    ← true when explicitly set OR ≥1 bulk action declares a `selectAll` endpoint
  //   bulkActionsEnabled← true when actions.bulk is non-empty
  const selectionEnabled = ext.selection != null;
  const hasBulkActions = bulkActions.length > 0;
  const hasSelectAllEndpoint = bulkActions.some((a) => a.selectAll != null);

  const selection: InternalFeaturesConfig['selection'] = selectionEnabled
    ? {
      enabled: true,
      keyField: ext.selection!.keyField,
      showCheckbox: true,
      multiSelect: true,
      selectAllPages: ext.selection!.selectAllPages === true || hasSelectAllEndpoint,
      bulkActionsEnabled: hasBulkActions,
      selectable: ext.selection!.selectable ?? null,
    }
    : { ...base.selection };

  // ── GroupBy ───────────────────────────────────────────────────────────────
  const groupBy =
    ext.groupBy?.fields != null && ext.groupBy.fields.length > 0
      ? {
        fields: [...ext.groupBy.fields],
        expandMode:
          ext.groupBy.expandMode ??
          ((ext.groupBy.defaultExpanded ? 'expanded' : 'collapsed') as
            | 'expanded'
            | 'collapsed'
            | 'alwaysExpanded'),
        hideGroupedColumn: ext.groupBy.hideGroupedColumn ?? false,
      }
      : null;

  return {
    columnPreferences,
    pagination: {
      defaultSize: pag.defaultSize ?? base.pagination.defaultSize,
      options: pag.options != null ? [...pag.options] : [...base.pagination.options],
      mode: pag.mode ?? base.pagination.mode,
    },
    selection,
    search: {
      enabled: ext.search?.enabled ?? base.search.enabled,
      debounce: ext.search?.debounce ?? base.search.debounce,
      placeholder: ext.search?.placeholder ?? base.search.placeholder,
    },
    defaultSort: ext.defaultSort ?? null,
    editing: {
      bulkSave: ed.bulkSave ?? base.editing.bulkSave,
      externalControl: ed.externalControl ?? base.editing.externalControl,
      patchEditedRow: ed.patchEditedRow ?? null,
      validateEditedRow: ed.validateEditedRow ?? null,
    },
    groupBy,
    rows: {
      height: rowsExt.height ?? base.rows.height,
      maxHeight: rowsExt.maxHeight ?? base.rows.maxHeight,
      striped: rowsExt.striped ?? base.rows.striped,
      subRows,
    },
    virtualScroll: (() => {
      const vs = ext.virtualScroll ?? {};
      const legacyVs = pag.virtualScroll;
      const paginationMode = pag.mode ?? base.pagination.mode;
      const resolvedPageSize = pag.defaultSize ?? base.pagination.defaultSize;
      const autoEnabled = paginationMode === 'infiniteScroll';

      return {
        enabled: vs.enabled ?? legacyVs?.enabled ?? autoEnabled,
        estimatedRowHeight: vs.estimatedRowHeight ?? legacyVs?.estimatedRowHeight ?? base.virtualScroll.estimatedRowHeight,
        overscan: vs.overscan ?? legacyVs?.overscan ?? base.virtualScroll.overscan,
        threshold: base.virtualScroll.threshold,
        pageSize: vs.pageSize ?? resolvedPageSize,
        maxCachedPages: vs.maxCachedPages ?? base.virtualScroll.maxCachedPages,
        fetchDebounce: vs.fetchDebounce ?? base.virtualScroll.fetchDebounce,
      };
    })(),
    rowPinning: ext.rowPinning
      ? { top: ext.rowPinning.top ?? null, bottom: ext.rowPinning.bottom ?? null }
      : null,
    filterGrid: { columns: ext.filterGrid?.columns ?? 2 },
    layout: { noPadding: ext.layout?.noPadding ?? false },
    emptyFilterMessage: ext.emptyFilterMessage ?? 'Search or apply filters to view results.',
    footer: ext.footer?.columns?.length
      ? {
        columns: ext.footer.columns.map((c) => ({ ...c })),
        style: ext.footer.style ?? 'default',
      }
      : null,
    backLink: ext.backLink?.path
      ? { path: ext.backLink.path, label: ext.backLink.label ?? 'Go Back' }
      : null,
  };
}

function normalizeActionDialog(action: ActionDef): InternalActionDef['dialog'] {
  const c = action.dialog ?? action.preflightDialog ?? action.confirm;
  if (c == null) return null;
  const kind = c.kind === 'alert' ? 'alert' : 'confirm';
  return {
    kind,
    title: c.title,
    message: c.message,
    confirmLabel: c.confirmLabel ?? (kind === 'alert' ? 'OK' : 'Confirm'),
    cancelLabel: c.cancelLabel ?? 'Cancel',
    variant: c.variant ?? 'info',
  };
}

function inferDefaultOnSuccess(actionType: string | undefined): OnSuccessAction {
  switch (actionType) {
    case 'direct':
      return 'toastAndRefetch';
    case 'export':
      return 'toast';
    case 'import':
      return 'toastAndRefetch';
    case 'modal':
      return 'none';
    case 'navigate':
      return 'none';
    case 'newTab':
      return 'none';
    default:
      return 'none';
  }
}

export function normalizeAction(action: ActionDef, index: number, idPrefix: string): InternalActionDef {
  const id = action.id ?? `${idPrefix}-${slugify(action.label)}-${index}`;
  const actionType = action.actionType;
  const method = action.method ?? (actionType === 'direct' ? 'POST' : 'GET');

  return {
    id,
    semanticId: action.semanticId ?? null,
    label: action.label,
    icon: action.icon ?? null,
    variant: action.variant ?? 'default',
    actionType,
    execution: action.execution ?? 'api',
    endpoint: action.endpoint ?? null,
    method,
    payload: action.payload != null ? { ...action.payload } : null,
    path: action.path ?? null,
    options: action.options != null ? action.options.map(o => ({ ...o })) : null,
    state: action.state != null ? { ...action.state } : null,
    url: action.url ?? null,
    fileName: action.fileName ?? null,
    fileKey: action.fileKey ?? null,
    permission: action.permission != null ? { ...action.permission } : null,
    requiresSelection: action.requiresSelection === true,
    visibilityCondition: action.visibilityCondition ?? null,
    disabledWhen: action.disabledWhen ?? null,
    dialog: normalizeActionDialog(action),
    onSuccess: action.onSuccess ?? inferDefaultOnSuccess(actionType),
    responseUrl: action.responseUrl ?? null,
    backgroundJobConfig: action.backgroundJobConfig != null ? { ...action.backgroundJobConfig } : null,
    successMessage: action.successMessage ?? null,
    errorMessage: action.errorMessage ?? null,
    selectAll:
      action.selectAll != null
        ? {
          endpoint: action.selectAll.endpoint,
          method: action.selectAll.method ?? 'POST',
          payload: action.selectAll.payload != null ? { ...action.selectAll.payload } : null,
          onSuccess: action.selectAll.onSuccess ?? null,
          successMessage: action.selectAll.successMessage ?? null,
          responseUrl: action.selectAll.responseUrl ?? null,
          backgroundJobConfig:
            action.selectAll.backgroundJobConfig != null
              ? { ...action.selectAll.backgroundJobConfig }
              : null,
        }
        : null,
  };
}

export function normalizeActionList(
  list: ActionDef[] | null | undefined,
  idPrefix: string
): InternalActionDef[] {
  return (list ?? []).map((a, i) => normalizeAction(a, i, idPrefix));
}

function isToolbarGroup(item: ActionDef | ToolbarTemplateDef): item is ToolbarTemplateDef {
  return 'children' in item && Array.isArray((item as ToolbarTemplateDef).children);
}

function normalizeToolbarButton(
  def: ToolbarButtonDef,
  index: number,
  idPrefix: string,
): InternalToolbarButton {
  const base = normalizeAction(def as ActionDef, index, idPrefix);
  const { variant: _discarded, ...rest } = base;
  return {
    ...rest,
    variant: def.variant ?? 'secondary',
  };
}

function normalizeToolbarTemplates(
  templates: ToolbarTemplateDef[] | null | undefined,
): InternalToolbarTemplate[] {
  if (!templates) return [];
  return templates.map((t, i) => ({
    label: t.label,
    icon: t.icon ?? null,
    type: t.type,
    semanticId: t.semanticId ?? null,
    children: normalizeActionList(t.children, `toolbar-template-${i}`),
  }));
}

/**
 * Splits a legacy `secondary` array into flat toolbar buttons and template groups.
 */
function splitLegacySecondary(
  secondary: Array<ActionDef | ToolbarTemplateDef> | null | undefined,
): { flat: InternalToolbarButton[]; groups: InternalToolbarTemplate[] } {
  const flat: InternalToolbarButton[] = [];
  const groups: InternalToolbarTemplate[] = [];

  if (!secondary) return { flat, groups };

  let flatIndex = 0;
  secondary.forEach((item) => {
    if (isToolbarGroup(item)) {
      groups.push({
        label: item.label,
        icon: item.icon ?? null,
        type: item.type,
        semanticId: item.semanticId ?? null,
        children: normalizeActionList(item.children, `toolbar-group-${groups.length}`),
      });
    } else {
      flat.push(normalizeToolbarButton(item as ToolbarButtonDef, flatIndex++, 'toolbar-secondary'));
    }
  });

  return { flat, groups };
}

/**
 * Detects whether the schema uses the new `buttons`/`templates` API or the
 * legacy `primary`/`secondary` API, and normalizes both into the unified
 * internal representation.
 */
function normalizeActions(
  actions: ActionConfig | null | undefined
): InternalTableSchema['actions'] {
  const a = actions ?? {};
  const subRowSource = a.subRow ?? (a as Record<string, unknown>).group as ActionDef[] | undefined;

  const toolbar = a.toolbar;
  const usesNewApi = toolbar?.buttons != null || toolbar?.templates != null;

  let buttons: InternalToolbarButton[];
  let templates: InternalToolbarTemplate[];

  if (usesNewApi) {
    buttons = (toolbar?.buttons ?? []).map((b, i) =>
      normalizeToolbarButton(b, i, 'toolbar-button'),
    );
    templates = normalizeToolbarTemplates(toolbar?.templates);
  } else {
    const { flat: secondaryFlat, groups: toolbarGroups } = splitLegacySecondary(toolbar?.secondary);

    buttons = [];
    if (toolbar?.primary != null) {
      const base = normalizeAction(toolbar.primary, 0, 'toolbar-primary');
      buttons.push({ ...base, variant: 'primary' as const });
    }
    secondaryFlat.forEach((btn) => {
      buttons.push(btn);
    });
    templates = toolbarGroups;
  }

  const primary = buttons.find((b) => b.variant === 'primary') ?? null;
  const secondary = buttons.filter((b) => b.variant !== 'primary');

  return {
    row: normalizeActionList(a.row, 'row'),
    bulk: normalizeActionList(a.bulk, 'bulk'),
    subRow: normalizeActionList(subRowSource, 'subrow'),
    toolbar: {
      buttons,
      templates,
      primary,
      secondary,
      groups: templates,
    },
  };
}

function normalizeSaveConfirm(c: ConfirmConfig | null | undefined): InternalConfirmConfig | null {
  if (c == null) return null;
  return {
    title: c.title,
    message: c.message,
    confirmLabel: c.confirmLabel ?? 'Confirm',
    cancelLabel: c.cancelLabel ?? 'Cancel',
    variant: c.variant ?? 'info',
    titleAlign: c.titleAlign,
    messageAlign: c.messageAlign,
  };
}

const DEFAULT_REQUEST_PARAMS: Record<string, string> = {
  Filter: 'table.searchText',
  MaxResultCount: 'table.pageSize',
  SkipCount: 'table.pageOffset',
  Sorting: 'table.sorting',
};

const DEFAULT_RESPONSE_MAPPING = {
  items: 'items',
  totalCount: 'totalCount',
  hasMoreItems: 'hasMoreItems',
};

function normalizeContinuationCursor(
  raw: ContinuationCursorConfig | null | undefined
): ContinuationCursorConfig | null {
  if (raw == null || typeof raw !== 'object') return null;
  const queryKey = typeof raw.queryKey === 'string' ? raw.queryKey.trim() : '';
  const fieldPath = typeof raw.fieldPath === 'string' ? raw.fieldPath.trim() : '';
  if (!queryKey || !fieldPath) return null;
  const f = raw.format;
  const format: NonNullable<ContinuationCursorConfig['format']> =
    f === 'iso' || f === 'mmddyyyy' || f === 'preserve' ? f : 'preserve';
  return { queryKey, fieldPath, format };
}

function normalizeDataSource(
  ds: DataSourceConfig | null | undefined
): InternalDataSourceConfig | null {
  if (ds == null) return null;

  const requestParams = {
    ...DEFAULT_REQUEST_PARAMS,
    ...(ds.requestParams ?? {}),
  };

  const continuationRaw = ds.continuationCursor ?? ds.loadMoreCursor ?? undefined;

  return {
    endpoint: ds.endpoint,
    method: ds.method ?? 'GET',
    rowIdField: ds.rowIdField,
    fetchPolicy: ds.fetchPolicy ?? 'always',
    responseMapping: {
      items: ds.responseMapping?.items ?? DEFAULT_RESPONSE_MAPPING.items,
      totalCount: ds.responseMapping?.totalCount ?? DEFAULT_RESPONSE_MAPPING.totalCount,
      hasMoreItems: ds.responseMapping?.hasMoreItems ?? DEFAULT_RESPONSE_MAPPING.hasMoreItems,
    },
    requestParams,
    itemsTransformId: ds.itemsTransformId ?? null,
    staticQueryParams: { ...(ds.staticQueryParams ?? {}) },
    pathParams: { ...(ds.pathParams ?? {}) },
    continuationCursor: normalizeContinuationCursor(continuationRaw),
    filters: (ds.filters ?? []).map((f) => ({ ...f })) as FilterDef[],
    saveEndpoint: ds.saveEndpoint ?? null,
    saveMethod: ds.saveMethod ?? 'POST',
    onSaveSuccess: ds.onSaveSuccess ?? 'toast',
    saveSuccessMessage: ds.saveSuccessMessage ?? null,
    confirmBeforeSave: normalizeSaveConfirm(ds.confirmBeforeSave),
    savePayloadMapper: ds.savePayloadMapper ?? null,
    saveActionId: ds.saveActionId ?? null,
  };
}

function normalizeDataInjection(
  targetField: string,
  def: DataSourceDef,
  index: number
): InternalDataInjection {
  return {
    id: `injection-${targetField}-${index}`,
    url: def.url,
    method: def.method ?? 'GET',
    targetField,
    mapper: { ...def.mapper },
    body: def.body != null ? { ...def.body } : null,
    cacheDuration: def.cacheDuration ?? 300_000,
  };
}

function extractDataInjections(columns: ExternalColumnDef[]): InternalDataInjection[] {
  const out: InternalDataInjection[] = [];
  columns.forEach((col, index) => {
    const ds = col.dataSource;
    if (ds == null) return;
    out.push(normalizeDataInjection(col.field, ds, index));
  });
  return out;
}

function normalizeSubRows(
  sub: SubRowConfig | null | undefined,
  parentColumns?: InternalColumnDef[],
): {
  config: InternalSubRowConfig | null;
  columns: InternalColumnDef[];
  injections: InternalDataInjection[];
} {
  if (sub == null) {
    return { config: null, columns: [], injections: [] };
  }

  const columns = sub.columns.map((c, i) => {
    const col = normalizeColumn(c);
    // When inheritAlignment is enabled and the sub-row column didn't specify
    // its own alignment, inherit from the parent column at the visual position.
    // Visual position accounts for the indent cell shifting sub-cols right by 1.
    if (sub.inheritAlignment !== false && c.align == null && parentColumns) {
      const parentIdx = (sub.indent !== false ? 1 : 0) + i;
      if (parentIdx < parentColumns.length) {
        return { ...col, align: parentColumns[parentIdx].align } as InternalColumnDef;
      }
    }
    return col;
  });
  const injections = extractDataInjections(sub.columns);

  // Process sub-row-level dataSources as fallback for editable asyncSelect
  // columns that don't have their own column-level dataSource.
  if (sub.dataSources && sub.dataSources.length > 0) {
    const coveredFields = new Set(injections.map((inj) => inj.targetField));
    const needsDataSource = sub.columns.filter(
      (c) => typeof c.editable === 'object' && c.editable?.type === 'asyncSelect' && !c.dataSource && !coveredFields.has(c.field)
    );
    const availableSources = [...sub.dataSources];
    needsDataSource.forEach((col) => {
      const ds = availableSources.shift();
      if (ds) {
        injections.push(normalizeDataInjection(col.field, ds, injections.length));
      }
    });
  }

  const actions = normalizeActionList(sub.actions, 'subrow-row');

  // Resolve expandMode: new property takes precedence, fall back to legacy flags
  let resolvedExpandMode: InternalSubRowConfig['expandMode'] = 'alwaysOpen';
  if (sub.expandMode) {
    resolvedExpandMode = sub.expandMode;
  } else if (sub.expand) {
    const { defaultExpanded, singleExpand } = sub.expand;
    if (singleExpand) {
      resolvedExpandMode = 'accordion';
    } else if (defaultExpanded) {
      resolvedExpandMode = 'alwaysOpen';
    } else {
      resolvedExpandMode = 'collapsed';
    }
  }

  const config: InternalSubRowConfig = {
    dataField: sub.dataField,
    columns,
    actions,
    expandMode: resolvedExpandMode,
    indent: sub.indent ?? true,
  };

  return { config, columns, injections };
}

function deepFreeze<T>(obj: T): T {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }
  const o = obj as object;
  if (Object.isFrozen(o)) {
    return obj;
  }
  Object.freeze(o);
  if (Array.isArray(o)) {
    o.forEach((item) => {
      if (item != null && typeof item === 'object') deepFreeze(item);
    });
  } else {
    for (const key of Object.getOwnPropertyNames(o)) {
      const v = (o as Record<string, unknown>)[key];
      if (v != null && typeof v === 'object') deepFreeze(v);
    }
  }
  return obj;
}

/**
 * Main entry — transforms an external table schema into the immutable internal representation.
 */
export function normalizeSchema(
  external: ExternalTableSchema,
  _pluginRegistry?: PluginRegistry
): InternalTableSchema {
  const meta = external.metadata ?? {};

  const mainColumnsExternal = external.columns ?? [];
  const columns = mainColumnsExternal.map((c) => normalizeColumn(c));
  const dataInjections = extractDataInjections(mainColumnsExternal);

  const {
    config: subRowConfig,
    columns: subRowColumns,
    injections: subRowDataInjections,
  } = normalizeSubRows(external.features?.rows?.subRows, columns);

  const features = normalizeFeatures(
    external.features,
    subRowConfig,
    columns,
    external.actions?.bulk ?? []
  );
  const dataSource = normalizeDataSource(external.dataSource);
  const actions = normalizeActions(external.actions);
  const computeRules = [...(external.computeRules ?? [])];

  const title = meta.title ?? '';
  const entity = meta.entity ?? '';
  const description = meta.description ?? '';

  const internal: InternalTableSchema = {
    version: '2.0',
    title,
    entity,
    description,
    dataSource,
    columns,
    subRowColumns,
    actions,
    features,
    computeRules,
    dataInjections,
    subRowDataInjections,
    testIds: {},
  };

  return deepFreeze(internal);
}
